/**
 * What a report template can be about, and the building blocks each kind of
 * template is put together from — the same catalogue the old UI offers.
 *
 * A template has one report type. The type decides which object a report is
 * run against (a line item, an order, an ad unit…) and which components can be
 * added to it. Each component carries its own default parameters: a heading,
 * the stat columns it shows, how many rows it lists and so on. The report
 * engine reads those parameters back as plain strings and booleans.
 */

export type ReportType =
  | 'LINE_ITEM'
  | 'LINE_ITEM_LIST'
  | 'ADVERTISER'
  | 'CREATIVE_SET'
  | 'NETWORK'
  | 'SITE'
  | 'AD_UNIT'
  | 'SITE_GROUP'
  | 'EARNINGS_ACCOUNT'
  | 'SALESPERSON'
  | 'SELF_SERVICE'

export type ReportFormat = 'HTML' | 'CSV'

/** The stat columns a component shows, each on or off. */
export type ReportFields = Record<string, boolean>

export interface ReportComponentParameters {
  heading?: string
  text?: string
  fields?: ReportFields
  limit?: number
  dateFormat?: string
  dateTimeFormatLineItem?: string
  sharedYAxis?: boolean
  height?: number
  chartType?: 'line' | 'bar' | 'column'
  hideCreativesWithoutStats?: boolean
  hideAllCreatives?: boolean
  onlySites?: boolean
  [key: string]: unknown
}

/** What a component draws — picks its icon and groups the add dialog. */
export type ReportComponentKind =
  | 'description'
  | 'totals'
  | 'breakdown'
  | 'chart'
  | 'top'
  | 'preview'
  | 'text'

export interface ReportComponentDefinition {
  id: string
  kind: ReportComponentKind
  /** Whether the component also renders in a CSV report. */
  csv: boolean
  parameters: ReportComponentParameters
  /** Field labels that read differently here — an advertiser pays "cost", not "earnings". */
  translationOverrides?: Record<string, string>
}

/* ── Field sets ──────────────────────────────────────────────────────── */

const selfServiceFields: ReportFields = {
  rendered: true,
  visibles: true,
  visibility: true,
  viewables: true,
  viewability: true,
  clicks: true,
  ctr: true,
  earnings: false,
  uniqueUsers: true,
}

const selfServiceTotalsFields: ReportFields = { ...selfServiceFields, billedAmount: false }

const topCreativeFields: ReportFields = {
  externalId: false,
  impressions: true,
  measurableImpressions: false,
  rendered: true,
  visibles: true,
  visibility: true,
  viewables: true,
  viewability: true,
  clicks: true,
  ctr: true,
  uniqueUsers: true,
  earnings: true,
  fee: false,
  techFee: false,
  eCpm: true,
  customEvents: false,
  customEventsAverages: false,
}

const defaultFields: ReportFields = Object.fromEntries(
  Object.entries(topCreativeFields).filter(([key]) => key !== 'externalId')
)

const orderBreakdownFields: ReportFields = {
  ...defaultFields,
  order: true,
  advertiser: true,
  vatId: false,
  creationDate: false,
  startDate: false,
  endDate: false,
  billedAmount: false,
}

const lineItemTotalsFields: ReportFields = { ...defaultFields, billedAmount: false }

const networkFields: ReportFields = {
  impressions: true,
  measurableImpressions: false,
  rendered: true,
  clicks: true,
  visibles: true,
  visibility: true,
  viewables: true,
  viewability: true,
  ctr: true,
  uniqueUsers: true,
  earnings: true,
  eCpm: true,
  customEvents: false,
  customEventsAverages: false,
}

const creativeBreakdownFields: ReportFields = {
  externalId: false,
  impressions: true,
  measurableImpressions: false,
  rendered: true,
  clicks: true,
  visibles: true,
  visibility: true,
  viewables: true,
  viewability: true,
  ctr: true,
  uniqueUsers: true,
  earnings: true,
  fee: false,
  techFee: false,
  eCpm: true,
  lineItem: true,
  order: true,
  advertiser: true,
  customEvents: false,
  customEventsAverages: false,
}

const targetingFields: ReportFields = {
  impressions: true,
  measurableImpressions: false,
  rendered: true,
  visibles: true,
  visibility: true,
  viewables: true,
  viewability: true,
  clicks: true,
  ctr: true,
}

const pubFields: ReportFields = {
  requests: true,
  positiveRequests: true,
  matchRate: true,
  impressions: true,
  measurableImpressions: false,
  rendered: true,
  visibles: true,
  visibility: true,
  viewables: true,
  viewability: true,
  clicks: true,
  ctr: true,
  uniqueUsers: true,
  earnings: true,
  revenueShare: false,
  eCpm: true,
  customEvents: false,
  customEventsAverages: false,
}

/* ── Component builders ──────────────────────────────────────────────── */

/** Advertiser-side components call earnings "cost". */
const ADV = { earnings: 'cost' }

function stats(
  heading: string,
  fields: ReportFields = defaultFields,
  extras: ReportComponentParameters = {}
): ReportComponentParameters {
  return { heading, fields: { ...fields }, ...extras }
}

function top(heading: string, fields: ReportFields, extras: ReportComponentParameters = {}) {
  return stats(heading, fields, { limit: 20, ...extras })
}

function daily(heading: string, fields?: ReportFields) {
  return stats(heading, fields, { dateFormat: '' })
}

function chart(heading: string, fields?: ReportFields) {
  return stats(heading, fields, { sharedYAxis: false, height: 500, chartType: 'line' })
}

function preview(heading: string, includeHideAll = false): ReportComponentParameters {
  return {
    heading,
    hideCreativesWithoutStats: false,
    ...(includeHideAll ? { hideAllCreatives: false } : {}),
  }
}

const TEXT: ReportComponentParameters = { text: '' }

function def(
  id: string,
  kind: ReportComponentKind,
  csv: boolean,
  parameters: ReportComponentParameters,
  translationOverrides?: Record<string, string>
): ReportComponentDefinition {
  return { id, kind, csv, parameters, translationOverrides }
}

/* ── The catalogue ───────────────────────────────────────────────────── */

const COMPONENT_LIST: ReportComponentDefinition[] = [
  // Creative set
  def('lineitemdescriptionreportcomponentcreativeset', 'description', false, preview('Creative Set Description', true), ADV),
  def('lineitemtotalsreportcomponentcreativeset', 'totals', true, stats('CreativeSet Totals', lineItemTotalsFields), ADV),
  def('textreportcomponentcreativeset', 'text', false, TEXT),
  def('dailybreakdownreportcomponentcreativeset', 'breakdown', true, daily('Daily Breakdown'), ADV),
  def('dailybreakdownchartreportcomponentcreativeset', 'chart', false, chart('Daily Breakdown Chart'), ADV),
  def('topcreativesreportcomponentcreativeset', 'top', true, stats('Creatives', topCreativeFields), ADV),
  def('creativebreakdownreportcomponentcreativeset', 'breakdown', true, stats('Creative Breakdown', creativeBreakdownFields), ADV),
  def('topadunitsreportcomponentcreativeset', 'top', true, top('Top %n% Ad Units', targetingFields), ADV),
  def('toplocationsreportcomponentcreativeset', 'top', true, top('Top %n% Locations', targetingFields), ADV),
  def('topmobilebrandsreportcomponentcreativeset', 'top', true, top('Top %n% Android Brands', targetingFields), ADV),
  def('topoperatingsystemsreportcomponentcreativeset', 'top', true, top('Top %n% Operating Systems', targetingFields), ADV),
  def('topkeyvaluesreportcomponentcreativeset', 'top', true, top('Top %n% Key-Values', targetingFields), ADV),
  def('topcategoriesreportcomponentcreativeset', 'top', true, top('Top %n% Categories', targetingFields), ADV),
  def('topdevicetypesreportcomponentcreativeset', 'top', true, top('Top %n% Device Types', targetingFields), ADV),

  // Line item
  def('lineitemdescriptionreportcomponentlineitem', 'description', false, preview('Line Item Description', true), ADV),
  def('lineitemtotalsreportcomponentlineitem', 'totals', true, stats('Line Item Totals', lineItemTotalsFields), ADV),
  def('textreportcomponentlineitem', 'text', false, TEXT),
  def('dailybreakdownreportcomponentlineitem', 'breakdown', true, daily('Daily Breakdown'), ADV),
  def('dailybreakdownchartreportcomponentlineitem', 'chart', false, chart('Daily Breakdown Chart'), ADV),
  def('targetingbreakdownreportcomponentlineitem', 'breakdown', true, stats('Targeting Breakdown', targetingFields), ADV),
  def('topcreativesreportcomponentlineitem', 'top', true, stats('Creatives', topCreativeFields), ADV),
  def('creativepreviewreportcomponentlineitem', 'preview', false, preview('Creative Previews'), ADV),
  def('creativedailybreakdownreportcomponentlineitem', 'breakdown', true, stats('Daily Creative Breakdown', topCreativeFields), ADV),
  def('creativebreakdownreportcomponentlineitem', 'breakdown', true, stats('Creative Breakdown', creativeBreakdownFields), ADV),
  def('topadunitsreportcomponentlineitem', 'top', true, top('Top %n% Ad Units', targetingFields), ADV),
  def('topsitesreportcomponentlineitem', 'top', true, top('Top %n% Sites', targetingFields, { onlySites: false }), ADV),
  def('toplocationsreportcomponentlineitem', 'top', true, top('Top %n% Locations', targetingFields), ADV),
  def('topmobilebrandsreportcomponentlineitem', 'top', true, top('Top %n% Android Brands', targetingFields), ADV),
  def('topoperatingsystemsreportcomponentlineitem', 'top', true, top('Top %n% Operating Systems', targetingFields), ADV),
  def('topkeyvaluesreportcomponentlineitem', 'top', true, top('Top %n% Key-Values', targetingFields), ADV),
  def('topcategoriesreportcomponentlineitem', 'top', true, top('Top %n% Categories', targetingFields), ADV),
  def('topdevicetypesreportcomponentlineitem', 'top', true, top('Top %n% Device Types', targetingFields), ADV),

  // Self-service campaign
  def('campaigndescriptionreportcomponentselfservice', 'description', false, { heading: 'Campaign Description' }, ADV),
  def('campaigntotalsreportcomponentselfservice', 'totals', true, stats('Campaign Totals', selfServiceTotalsFields), ADV),
  def('dailybreakdownchartreportcomponentselfservice', 'chart', false, chart('Daily Breakdown Chart', selfServiceFields), ADV),
  def('dailybreakdownreportcomponentselfservice', 'breakdown', true, stats('Daily Breakdown', selfServiceFields), ADV),
  def('topsitesreportcomponentselfservice', 'top', true, top('Top %n% Sites', targetingFields, { onlySites: true }), ADV),

  // Order / line item list
  def('orderdescriptionreportcomponentlineitemlist', 'description', false, { heading: 'Order Description' }, ADV),
  def('ordertotalsreportcomponentlineitemlist', 'totals', true, stats('Order Totals', lineItemTotalsFields), ADV),
  def('dailybreakdownreportcomponentlineitemlist', 'breakdown', true, daily('Daily Breakdown'), ADV),
  def('dailybreakdownchartreportcomponentlineitemlist', 'chart', true, chart('Daily Breakdown Chart'), ADV),
  def('lineitembreakdownreportcomponentlineitemlist', 'breakdown', true, stats('Line Item Breakdown', orderBreakdownFields), ADV),
  def('creativepreviewreportcomponentlineitemlist', 'preview', false, preview('Creative Previews'), ADV),
  def('creativebreakdownreportcomponentlineitemlist', 'breakdown', true, stats('Creative Breakdown', creativeBreakdownFields), ADV),
  def('textreportcomponentlineitemlist', 'text', false, TEXT, ADV),
  def('toplocationsreportcomponentlineitemlist', 'top', true, top('Top %n% Locations', targetingFields), ADV),

  // Advertiser
  def('advertiserdescriptionreportcomponentadvertiser', 'description', false, { heading: 'Advertiser Description' }, ADV),
  def('advertisertotalsreportcomponentadvertiser', 'totals', true, stats('Advertiser Totals', lineItemTotalsFields), ADV),
  def('dailybreakdownreportcomponentadvertiser', 'breakdown', true, daily('Daily Breakdown'), ADV),
  def('dailybreakdownchartreportcomponentadvertiser', 'chart', false, chart('Daily Breakdown Chart'), ADV),
  def('orderbreakdownreportcomponentadvertiser', 'breakdown', true, stats('Order Breakdown', orderBreakdownFields), ADV),
  def('lineitembreakdownreportcomponentadvertiser', 'breakdown', true, stats('Line Item Breakdown', orderBreakdownFields), ADV),
  def('creativebreakdownreportcomponentadvertiser', 'breakdown', true, stats('Creative Breakdown', creativeBreakdownFields), ADV),
  def('textreportcomponentadvertiser', 'text', false, TEXT, ADV),

  // Network
  def('networkdescriptionreportcomponentnetwork', 'description', false, { heading: 'Network Description' }),
  def('networktotalsreportcomponentnetwork', 'totals', true, stats('Network Totals', networkFields)),
  def('networkbreakdownreportcomponentnetwork', 'breakdown', true, stats('Network Breakdown', networkFields, { dateFormat: '', dateTimeFormatLineItem: '' })),
  def('textreportcomponentnetwork', 'text', false, TEXT),

  // Ad unit
  def('adunitdescriptionreportcomponentadunit', 'description', false, { heading: 'Ad Unit Description' }),
  def('publishingtotalsreportcomponentadunit', 'totals', true, stats('Ad Unit Totals', pubFields)),
  def('dailybreakdownreportcomponentadunit', 'breakdown', true, daily('Daily Breakdown', pubFields)),
  def('dailybreakdownchartreportcomponentadunit', 'chart', false, chart('Daily Breakdown Chart', pubFields)),
  def('targetingbreakdownreportcomponentadunit', 'breakdown', true, stats('Targeting Breakdown', targetingFields)),
  def('textreportcomponentadunit', 'text', false, TEXT),

  // Salesperson
  def('salespersondescriptionreportcomponentsalesperson', 'description', false, { heading: 'Salesperson Description' }, ADV),
  def('dailybreakdownreportcomponentsalesperson', 'breakdown', true, daily('Daily Breakdown'), ADV),
  def('dailybreakdownchartreportcomponentsalesperson', 'chart', true, chart('Daily Breakdown Chart'), ADV),
  def('lineitembreakdownreportcomponentsalesperson', 'breakdown', true, stats('Line Item Breakdown', orderBreakdownFields), ADV),
  def('creativebreakdownreportcomponentsalesperson', 'breakdown', true, stats('Creative Breakdown', creativeBreakdownFields), ADV),
  def('advertisingtotalsreportcomponentsalesperson', 'totals', true, stats('Advertising Totals', lineItemTotalsFields), ADV),
  def('textreportcomponentsalesperson', 'text', false, TEXT, ADV),

  // Site
  def('adunitlistdescriptionreportcomponentsite', 'description', false, stats('Description', pubFields)),
  def('publishingtotalsreportcomponentsite', 'totals', true, stats('Site Totals', pubFields)),
  def('adunitbreakdownreportcomponentsite', 'breakdown', true, stats('Ad Unit Breakdown', pubFields)),
  def('dailybreakdownreportcomponentsite', 'breakdown', true, daily('Daily Breakdown', pubFields)),
  def('dailybreakdownchartreportcomponentsite', 'chart', false, chart('Daily Breakdown Chart', pubFields)),
  def('textreportcomponentsite', 'text', false, TEXT),

  // Site group
  def('adunitlistdescriptionreportcomponentsitegroup', 'description', false, stats('Description', pubFields)),
  def('publishingtotalsreportcomponentsitegroup', 'totals', true, stats('Site Group Totals', pubFields)),
  def('adunitbreakdownreportcomponentsitegroup', 'breakdown', true, stats('Ad Unit Breakdown', pubFields)),
  def('dailybreakdownreportcomponentsitegroup', 'breakdown', true, daily('Daily Breakdown', pubFields)),
  def('dailybreakdownchartreportcomponentsitegroup', 'chart', false, chart('Daily Breakdown Chart', pubFields)),
  def('textreportcomponentsitegroup', 'text', false, TEXT),

  // Earnings account
  def('adunitlistdescriptionreportcomponentearningsaccount', 'description', false, stats('Description', pubFields)),
  def('publishingtotalsreportcomponentearningsaccount', 'totals', true, stats('Earnings Account Totals', pubFields)),
  def('sitebreakdownreportcomponentearningsaccount', 'breakdown', true, stats('Site Breakdown', pubFields)),
  def('adunitbreakdownreportcomponentearningsaccount', 'breakdown', true, stats('Ad Unit Breakdown', pubFields)),
  def('dailybreakdownreportcomponentearningsaccount', 'breakdown', true, daily('Daily Breakdown', pubFields)),
  def('dailybreakdownchartreportcomponentearningsaccount', 'chart', true, chart('Daily Breakdown Chart', pubFields)),
  def('textreportcomponentearningsaccount', 'text', false, TEXT),
]

export const REPORT_COMPONENTS: Record<string, ReportComponentDefinition> = Object.fromEntries(
  COMPONENT_LIST.map((c) => [c.id, c])
)

/* ── Report types ────────────────────────────────────────────────────── */

export interface ReportTypeDefinition {
  id: ReportType
  /** Parameter naming the object a report of this type is run against. */
  idKey: string
  /** Search API type of that object; absent where there is no object to pick. */
  searchType?: string
  /** Whether a report of this type can cover the object's own flight. */
  hasFlight: boolean
  /** Components offered, in the order the add dialog lists them. */
  components: string[]
}

/**
 * Known to the report engine, so a template that has one still shows it, but
 * not offered for new templates — the old UI never listed them either.
 */
const NOT_OFFERED = new Set([
  'creativebreakdownreportcomponentlineitem',
  'creativebreakdownreportcomponentcreativeset',
])

const ids = (suffix: string) =>
  COMPONENT_LIST.filter(
    (c) => c.id.endsWith(`reportcomponent${suffix}`) && !NOT_OFFERED.has(c.id)
  ).map((c) => c.id)

export const REPORT_TYPES: Record<ReportType, ReportTypeDefinition> = {
  LINE_ITEM: { id: 'LINE_ITEM', idKey: 'lineItemId', searchType: 'LineItem', hasFlight: true, components: ids('lineitem') },
  LINE_ITEM_LIST: { id: 'LINE_ITEM_LIST', idKey: 'orderId', searchType: 'Order', hasFlight: true, components: ids('lineitemlist') },
  ADVERTISER: { id: 'ADVERTISER', idKey: 'advertiserId', searchType: 'Advertiser', hasFlight: true, components: ids('advertiser') },
  CREATIVE_SET: { id: 'CREATIVE_SET', idKey: 'creativeSetId', searchType: 'CreativeSet', hasFlight: true, components: ids('creativeset') },
  NETWORK: { id: 'NETWORK', idKey: 'networkId', hasFlight: false, components: ids('network') },
  SITE: { id: 'SITE', idKey: 'siteId', searchType: 'Site', hasFlight: false, components: ids('site') },
  AD_UNIT: { id: 'AD_UNIT', idKey: 'adUnitId', searchType: 'AdUnit', hasFlight: false, components: ids('adunit') },
  SITE_GROUP: { id: 'SITE_GROUP', idKey: 'siteGroupId', searchType: 'SiteGroup', hasFlight: false, components: ids('sitegroup') },
  EARNINGS_ACCOUNT: { id: 'EARNINGS_ACCOUNT', idKey: 'earningsAccountId', searchType: 'EarningsAccount', hasFlight: false, components: ids('earningsaccount') },
  SALESPERSON: { id: 'SALESPERSON', idKey: 'salesPersonId', searchType: 'User', hasFlight: false, components: ids('salesperson') },
  SELF_SERVICE: { id: 'SELF_SERVICE', idKey: 'lineItemId', searchType: 'LineItem', hasFlight: true, components: ids('selfservice') },
}

/** Every report type, in the order the old UI lists them. */
export const ALL_REPORT_TYPES = Object.keys(REPORT_TYPES) as ReportType[]

/** Report types a network can make templates for — self-service only on a self-service network. */
export function reportTypesFor(network: { suiPlatform?: boolean } | null | undefined): ReportType[] {
  return ALL_REPORT_TYPES.filter((type) => type !== 'SELF_SERVICE' || !!network?.suiPlatform)
}

/** Order and advertiser reports can also attach a line item report for each line item. */
export function hasChildTemplate(type: string | undefined): boolean {
  return type === 'LINE_ITEM_LIST' || type === 'ADVERTISER'
}

/** Parameter the advertiser report reads as "every active advertiser". */
export const ALL_ACTIVE_FLAG = 'ALL_ACTIVE'

/* ── Date formats a breakdown can print its dates in ────────────────── */

export type TimeFormat = 'NONE' | 'HOUR24' | 'AMPM'

export interface DateFormatOption {
  format: string
  example: string
}

const BASE_DATE_FORMATS: DateFormatOption[] = [
  { format: '', example: '' },
  { format: 'd/M/yyyy', example: '23/10/2018' },
  { format: 'd-M-yyyy', example: '23-10-2018' },
  { format: 'd.M.yyyy', example: '23.10.2018' },
  { format: 'd MMM yyyy', example: '23 Oct 2018' },
  { format: 'd-MMM-yyyy', example: '23-Oct-2018' },
  { format: 'd.MMM.yyyy', example: '23.Oct.2018' },
  { format: 'M/d/yyyy', example: '10/23/2018' },
  { format: 'M-d-yyyy', example: '10-23-2018' },
  { format: 'M.d.yyyy', example: '10.23.2018' },
  { format: 'yyyy/M/d', example: '2018/10/23' },
  { format: 'yyyy-M-d', example: '2018-10-23' },
  { format: 'yyyy.M.d', example: '2018.10.23' },
  { format: 'yyyy MMM d', example: '2018 Oct 23' },
  { format: 'yyyy-MMM-d', example: '2018-Oct-23' },
]

const TIME_SUFFIX: Record<TimeFormat, { format: string; example: string }> = {
  NONE: { format: '', example: '' },
  HOUR24: { format: ' k:mm', example: ' 18:49' },
  AMPM: { format: ' h:mm a', example: ' 6:49 PM' },
}

/** Date formats with the given clock appended; the first entry is "the user's locale". */
export function dateFormats(time: TimeFormat = 'NONE'): DateFormatOption[] {
  return BASE_DATE_FORMATS.map((f) =>
    f.format
      ? { format: f.format + TIME_SUFFIX[time].format, example: f.example + TIME_SUFFIX[time].example }
      : f
  )
}

/** The clock a stored date-time format uses. */
export function timeFormatOf(format: string | undefined): TimeFormat {
  if (!format) return 'HOUR24'
  if (format.includes('k')) return 'HOUR24'
  if (format.includes('h')) return 'AMPM'
  return 'NONE'
}

/** The same date format with another clock. */
export function withTimeFormat(format: string, time: TimeFormat): string {
  if (!format) return format
  const base = format.replace(/ (k:mm|h:mm a)$/, '')
  return base + TIME_SUFFIX[time].format
}
